import React, { useState, useRef, useEffect } from "react";
import { useGamification } from "../context/GamificationContext";
import { useChallenges } from "../context/ChallengesContext";
import { useTheme } from "../context/ThemeContext";
import { useNewPosts } from "../hooks/useNewPosts";
import ShareButton from "./shared/ShareButton";
import AchievementsModal from "./gamification/AchievementsModal";
import AnalyticsDashboard from "./analytics/AnalyticsDashboard";
import DailyChallenges from "./challenges/DailyChallenges";
import Leaderboard from "./social/Leaderboard";
import soundService from "../utils/soundService";
import "../components/gamification/Gamification.css";
import "../components/challenges/Challenges.css";

const Header = ({
  user,
  onLogout,
  currentGrade,
  showTranslator,
  showDictionary,
  showMessageBoard,
  showGradeDropdown,
  onGradeButtonClick,
  onGradeSelect,
  onTranslatorClick,
  onDictionaryClick,
  onMessageBoardClick,
  gradeDropdownRef
}) => {
  const { getAllAchievements } = useGamification();
  const { getCompletionStats } = useChallenges();
  const { isDarkMode, toggleTheme } = useTheme();
  const { newPostsCount } = useNewPosts(showMessageBoard);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showChallenges, setShowChallenges] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showSoundPanel, setShowSoundPanel] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const accountMenuRef = useRef(null);

  const achievements = getAllAchievements();
  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const challengeStats = getCompletionStats();

  // Close account menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setShowAccountMenu(false);
        setShowSoundPanel(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    soundService.setVolume(newVolume);
  };

  const testSinhalaSound = () => {
    const audio = new Audio('/audio/words/4LaF4La44LeK4La44LeP.mp3');
    audio.volume = volume;
    audio.play();
  };

  const testEnglishSound = () => {
    soundService.speakEnglishWord("Hello, welcome to the Sinhala learning app!");
  };

  const handleMenuItemClick = (action) => {
    action();
    setShowAccountMenu(false);
  };

  return (
    <>
      <header className="header-bar">
        {/* Brand */}
        <div className="header-brand">
          <span className="brand-flag">🇱🇰</span>
          <div className="brand-text">
            <span className="brand-title">Sringlish</span>
            <span className="brand-subtitle">Learn the Language of Sri Lanka</span>
          </div>
        </div>

        {user && (
          <>
            {/* Navigation Buttons */}
            <nav className="header-nav">
              <div className="nav-dropdown" ref={gradeDropdownRef}>
                <button
                  className={`nav-btn ${!showTranslator && !showDictionary && !showMessageBoard ? 'active' : ''}`}
                  onClick={onGradeButtonClick}
                >
                  <span className="nav-icon">📚</span>
                  <span className="nav-label">Grade {currentGrade}</span>
                  <span className="nav-arrow">{showGradeDropdown ? '▲' : '▼'}</span>
                </button>
                {showGradeDropdown && (
                  <div className="nav-dropdown-menu">
                    {[1, 2, 3, 4, 5, 6].map((grade) => (
                      <button
                        key={grade}
                        className={`nav-dropdown-item ${currentGrade === grade ? 'selected' : ''}`}
                        onClick={() => onGradeSelect(grade)}
                      >
                        <span className="grade-num">{grade}</span>
                        <span>Grade {grade}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                className={`nav-btn ${showTranslator ? 'active' : ''}`}
                onClick={onTranslatorClick}
              >
                <span className="nav-icon">🔍</span>
                <span className="nav-label">Translator</span>
              </button>

              <button
                className={`nav-btn ${showDictionary ? 'active' : ''}`}
                onClick={onDictionaryClick}
              >
                <span className="nav-icon">📖</span>
                <span className="nav-label">Dictionary</span>
              </button>

              <button
                className={`nav-btn ${showMessageBoard ? 'active' : ''}`}
                onClick={onMessageBoardClick}
              >
                <span className="nav-icon">💬</span>
                <span className="nav-label">Community</span>
                {newPostsCount > 0 && (
                  <span className="nav-badge">{newPostsCount > 9 ? '9+' : newPostsCount}</span>
                )}
              </button>
            </nav>

            {/* Right Controls */}
            <div className="header-controls">
              <ShareButton />

              {/* Account Dropdown */}
              <div className="account-dropdown" ref={accountMenuRef}>
                <button
                  className="account-btn"
                  onClick={() => {
                    setShowAccountMenu(!showAccountMenu);
                    setShowSoundPanel(false);
                  }}
                >
                  <span className="account-icon">👤</span>
                  <span className="account-name">
                    {user.isGuest ? 'Guest' : user.email.split('@')[0]}
                  </span>
                  <span className="dropdown-arrow">{showAccountMenu ? '▲' : '▼'}</span>
                </button>

                {showAccountMenu && (
                  <div className="account-menu">
                    <div className="account-menu-header">
                      <span className="menu-user-icon">{user.isGuest ? '👤' : '👨‍💻'}</span>
                      <div className="menu-user-info">
                        <span className="menu-user-name">
                          {user.isGuest ? 'Guest User' : user.email.split('@')[0]}
                        </span>
                        {!user.isGuest && (
                          <span className="menu-user-email">{user.email}</span>
                        )}
                      </div>
                    </div>

                    <div className="account-menu-divider"></div>

                    <button
                      className="account-menu-item"
                      onClick={() => handleMenuItemClick(() => setShowChallenges(true))}
                    >
                      <span className="menu-icon">🎯</span>
                      <span className="menu-label">Daily Challenges</span>
                      {challengeStats.unclaimed > 0 && (
                        <span className="menu-badge">{challengeStats.unclaimed}</span>
                      )}
                    </button>

                    <button
                      className="account-menu-item"
                      onClick={() => handleMenuItemClick(() => setShowLeaderboard(true))}
                    >
                      <span className="menu-icon">🏅</span>
                      <span className="menu-label">Leaderboard</span>
                    </button>

                    <button
                      className="account-menu-item"
                      onClick={() => handleMenuItemClick(() => setShowAchievements(true))}
                    >
                      <span className="menu-icon">🏆</span>
                      <span className="menu-label">Achievements</span>
                      {unlockedCount > 0 && (
                        <span className="menu-badge">{unlockedCount}</span>
                      )}
                    </button>

                    <button
                      className="account-menu-item"
                      onClick={() => handleMenuItemClick(() => setShowAnalytics(true))}
                    >
                      <span className="menu-icon">📊</span>
                      <span className="menu-label">Analytics & Progress</span>
                    </button>

                    <div className="account-menu-divider"></div>

                    <button
                      className="account-menu-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSoundPanel(!showSoundPanel);
                      }}
                    >
                      <span className="menu-icon">🔊</span>
                      <span className="menu-label">Sound Settings</span>
                      <span className="menu-arrow">{showSoundPanel ? '▼' : '▶'}</span>
                    </button>

                    {showSoundPanel && (
                      <div className="sound-submenu">
                        <div className="sound-setting">
                          <label>Volume: {Math.round(volume * 100)}%</label>
                          <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.1"
                            value={volume}
                            onChange={handleVolumeChange}
                          />
                        </div>
                        <div className="sound-test-btns">
                          <button onClick={testEnglishSound} className="test-btn-small">
                            Test English
                          </button>
                          <button onClick={testSinhalaSound} className="test-btn-small">
                            Test Sinhala
                          </button>
                        </div>
                      </div>
                    )}

                    <button
                      className="account-menu-item"
                      onClick={() => {
                        toggleTheme();
                      }}
                    >
                      <span className="menu-icon">{isDarkMode ? '☀️' : '🌙'}</span>
                      <span className="menu-label">{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                    </button>

                    <div className="account-menu-divider"></div>

                    <button
                      className="account-menu-item logout-item"
                      onClick={() => handleMenuItemClick(onLogout)}
                    >
                      <span className="menu-icon">🚪</span>
                      <span className="menu-label">{user.isGuest ? 'Exit Guest Mode' : 'Logout'}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </header>

      <AchievementsModal
        isOpen={showAchievements}
        onClose={() => setShowAchievements(false)}
      />

      <AnalyticsDashboard
        isOpen={showAnalytics}
        onClose={() => setShowAnalytics(false)}
      />

      <DailyChallenges
        isOpen={showChallenges}
        onClose={() => setShowChallenges(false)}
      />

      <Leaderboard
        isOpen={showLeaderboard}
        onClose={() => setShowLeaderboard(false)}
      />
    </>
  );
};

export default Header;
